import React from 'react'
import { useScroll } from 'ahooks'
import { match } from 'ts-pattern'
import { Button } from 'antd'
import { Trans, useLingui } from '@lingui/react/macro'

import { headerHeightCls } from './constants'
import styles from './index.module.scss'

import type { DefaultHeaderProps } from '@/layouts/type'

import Link from '@/components/Link'
import { useAppUIModal } from '@/contexts/AppUIModalContext'
import { useSignOut } from '@/contexts/AuthContext/hooks/signOut'
import { useSiteSpecificContext } from '@/contexts/SiteSpecificContext'
import Avatar from '@/components/Avatar'
import WebMenuEntry from '@/_blocks/Managements/_components/Menus/WebMenuEntry'
import { LanguageTab } from '@/components/LanguageTab'


import { useMemo, useState, useEffect } from 'react';
import { cls, tw } from 'tagged-classnames-free';
interface ExpandMenu {
  label: string
  href: string
  onClick?: () => void
  children?: Array<Pick<ExpandMenu, 'label' | 'href' | 'onClick'>>
}

interface CommonProps {
  onProfile?: () => void
  onSignOut?: () => void
  onSignUp?: () => void
  onSignIn?: () => void
  className?: string
  locales?: string[] | null
}
const Pc = (props: CommonProps) => {
  const { onProfile, onSignOut, onSignUp, onSignIn, className, locales } = props

  const { t } = useLingui()

  const { user, creator, isPaidUser, usage } = useSiteSpecificContext()
  const { userName, userImage, isSignIn } = user
  const currentCount = useMemo(() => {
    const { creditsTotalCount, creditsUseCount } = usage.countInfo
    return Math.max(creditsTotalCount - creditsUseCount, 0)
  }, [usage.countInfo])
  const { displayName } = creator
  const [userDisplayName, setUserDisplayName] = useState('')
  useEffect(() => {
    if (!user.isLoading && !creator.isLoading) {
      setUserDisplayName(creator.displayName || user.userName)
    }
  }, [creator, user])

  const { upgradePlan } = useAppUIModal()

  const showUpgradeModal = () => {
    upgradePlan.show({
      reason: t`Get More Credits & Premium Features`,
    })
  }

  const expandMenus = [
    { label: t`Home`, href: '/homepage' },
    {
      label: t`AI Song Maker`,
      href: '/ai-music-generator',
      children: [
        { label: t`AI Music Generator`, href: '/ai-music-generator' },
        { label: t`AI Rap Generator`, href: '/ai-rap-generator' },
        { label: t`AI Cover Generator`, href: '/ai-cover-generator' },
        { label: t`Text to Speech`, href: '/text-to-speech' },
      ],
    },
    { label: t`Resources`, href: '/hub' },
    { label: t`Reviews`, href: '/reviews' },
    !isSignIn
      ? { label: t`Pricing`, href: '/pricing', onClick: showUpgradeModal }
      : null,
    isSignIn && !isPaidUser
      ? { label: t`Upgrade`, href: '/pricing', onClick: showUpgradeModal }
      : null,
    usage.creditsUsage
      ? { label: 'count', href: '/pricing', onClick: showUpgradeModal }
      : null,
  ].filter(Boolean) as ExpandMenu[]

  return (
    <div
      className={cls`w-full items-center gap-2.5 px-4 xl:px-[22px] ${headerHeightCls} ${className}`}
    >
      <div className={`ml-auto flex items-center gap-3`}>
        <div className='flex items-center gap-8'>
          {expandMenus.map((item, index) => {
            if (item.children?.length) {
              return (
                <div key={item.label} className='group relative'>
                  <div className='hover:text-f-primary flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs font-medium sm:text-sm'>
                    <span>{item.label}</span>
                    <span className='i-com--down block size-4 text-current transition-transform duration-200 group-hover:rotate-180' />
                  </div>
                  <ul
                    className='bg-f-bg-container border-f-border-active invisible absolute left-1/2 top-7 z-[3] min-w-[180px] origin-top scale-y-95 rounded-lg border py-2 opacity-0 shadow-[0_4px_12px_rgba(0,0,0,.25)] transition-all duration-200 group-hover:visible group-hover:scale-y-100 group-hover:opacity-100'
                    style={{ transform: 'translateX(-50%)' }}
                  >
                    {item.children.map((subMenu) => (
                      <li key={`${item.label}-${subMenu.href}`}>
                        <Link
                          className='hover:bg-f-bg-active hover:text-f-primary block whitespace-nowrap px-3 py-2 text-xs font-medium sm:text-sm'
                          href={subMenu.href}
                          onClick={(e) => {
                            if (typeof subMenu.onClick === 'function') {
                              e.preventDefault()
                              subMenu.onClick()
                            }
                          }}
                        >
                          {subMenu.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            }
            return (
              <Link
                key={item.label}
                className='hover:text-f-primary whitespace-nowrap text-xs font-medium hover:cursor-pointer sm:text-sm'
                href={item.href}
                onClick={(e) => {
                  if (typeof item.onClick === 'function') {
                    e.preventDefault()
                    item.onClick()
                  }
                }}
              >
                {match(item.label)
                  .with('count', () => (
                    <span
                      className={cls`text-f-primary flex items-center gap-1`}
                    >
                      {currentCount}
                      <span className={cls`i-cus--tad-credits`} />
                    </span>
                  ))
                  .with('Upgrade', () => (
                    <Button type='primary' className={cls`rounded-[80px]`}>
                      <span className={cls`i-cus--tad-member-plus`}></span>
                      <Trans>Upgrade</Trans>
                    </Button>
                  ))
                  .otherwise(() => item.label)}
              </Link>
            )
          })}
          <ul className='flex gap-3'>
            <li className='user-valid:block hidden'>
              <div className='relative'>
                <Avatar
                  url={userImage}
                  name={userDisplayName}
                  className={cls`size-7 [&>img]:size-full [&>img]:object-cover`}
                />
                <ul className={styles.userMenuDropdown}>
                  {!locales || (locales && locales.length > 0) ? (
                    <LanguageTab title={t`Language`} icon='i-com--globe' />
                  ) : null}
                  <li onClick={onProfile} className='before:i-com--user'>
                    <Trans>Profile</Trans>
                  </li>
                  {isPaidUser ? (
                    <li className='before:i-com--wallet'>
                      <Link href={'/app/account/billing'}>
                        <Trans>Billing</Trans>
                      </Link>
                    </li>
                  ) : null}
                  <li
                    onClick={showUpgradeModal}
                    className='before:i-com--buy-cart'
                  >
                    <Trans>Pricing</Trans>
                  </li>
                  <WebMenuEntry />
                  <li className='before:i-com--support before:min-w-4'>
                    <Link href={'mailto:support@tad.ai'}>
                      <Trans>Contact Us</Trans>
                    </Link>
                  </li>
                  <li className='before:i-cus--tad-logout' onClick={onSignOut}>
                    <Trans>Logout</Trans>
                  </li>
                </ul>
              </div>
            </li>
            <li className='user-vague:block hidden'>
              <div className='flex h-7 items-center gap-8 text-xs font-semibold sm:text-sm'>
                <span
                  className='hover:text-f-primary whitespace-nowrap font-medium hover:cursor-pointer'
                  onClick={onSignIn}
                >
                  <Trans>Sign In</Trans>
                </span>
                <span
                  className='text-f-text-light primary-btn whitespace-nowrap rounded-full px-3 py-1'
                  onClick={onSignUp}
                >
                  <Trans>Sign Up</Trans>
                </span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

const Mobile = (props: CommonProps) => {
  const { onProfile, onSignOut, onSignUp, onSignIn, className } = props

  const [menuExpand, setMenuExpand] = useState(false)
  const [featureExpand, setFeatureExpand] = useState(false)

  const { user, creator } = useSiteSpecificContext()
  const { t } = useLingui()

  const { userName, userImage, isSignIn } = user
  const { displayName } = creator
  const [userDisplayName, setUserDisplayName] = useState('')
  useEffect(() => {
    if (!user.isLoading && !creator.isLoading) {
      setUserDisplayName(creator.displayName || user.userName)
    }
  }, [creator, user])
  return (
    <div
      className={cls` w-full items-center justify-between px-4 ${headerHeightCls} ${className}`}
    >
      <div className='flex items-center gap-3'>
        <div
          className='relative flex h-full items-center'
          onClick={() =>
            setMenuExpand((val) => {
              const next = !val
              if (!next) {
                setFeatureExpand(false)
              }
              return next
            })
          }
        >
          {menuExpand ? (
            <span className='i-com--x size-6' />
          ) : (
            <span className='i-com--menu size-6' />
          )}
        </div>
        {/* <Link href='/' className='flex items-center'>
          <CssIcon
            className='i-cus--tad-logo'
            style={{
              width: 74,
              height: 20,
            }}
          />
        </Link> */}
      </div>
      <div>
        {(displayName || userName) && isSignIn ? (
          <div className='relative'>
            <Avatar url={userImage} name={userDisplayName} />
            <ul className='bg-f-bg-container border-f-border-active [&_li:hover]:bg-f-bg-active [&_li:hover]:text-f-primary absolute right-0 top-7 w-[120px] origin-top scale-y-0 space-y-2 rounded-lg border p-3 text-sm opacity-0 transition hover:scale-y-100 hover:opacity-100 peer-hover:scale-y-100 peer-hover:opacity-100 [&_li:before]:size-4 [&_li:hover]:cursor-pointer [&_li]:flex [&_li]:items-center [&_li]:gap-1 [&_li]:rounded [&_li]:px-2 [&_li]:py-1'>
              <li onClick={onProfile} className='before:i-com--user'>
                <Trans>Profile</Trans>
              </li>
              <li className='before:i-com--wallet'>
                <Link href={'/app/account/billing'}>
                  <Trans>Billing</Trans>
                </Link>
              </li>
              <li className='before:i-com--buy-cart'>
                <Link href={'/pricing'}>
                  <Trans>Pricing</Trans>
                </Link>
              </li>
              <li className='before:i-cus--tad-logout' onClick={onSignOut}>
                <Trans>Logout</Trans>
              </li>
            </ul>
          </div>
        ) : (
          <div className='flex h-7 items-center gap-2 text-xs font-semibold'>
            <span
              className='hover:text-f-primary whitespace-nowrap font-medium hover:cursor-pointer'
              onClick={onSignIn}
            >
              <Trans>Sign In</Trans>
            </span>
            <span
              className='text-f-text-light primary-btn whitespace-nowrap rounded-full px-3 py-1'
              onClick={onSignUp}
            >
              <Trans>Sign Up</Trans>
            </span>
          </div>
        )}
      </div>
      <div
        className={tw`z-100 fixed bottom-0 left-0 top-[52px] flex w-screen flex-col overflow-hidden bg-[#000000] px-[52px] duration-300 ${menuExpand ? 'h-screen' : 'h-0'} `}
      >
        <div className='w-full border-b border-[#FFFFFF29] py-4'>
          <div
            className='hover:text-f-primary flex w-full cursor-pointer items-center justify-between whitespace-nowrap text-xs font-medium sm:text-sm'
            style={{ color: '#fff' }}
            onClick={() => setFeatureExpand((val) => !val)}
          >
            <span>
              <Trans>AI Song Maker</Trans>
            </span>
            <span
              className={tw`i-com--down block size-4 text-current transition-transform duration-200 ${featureExpand ? 'rotate-180' : ''}`}
            />
          </div>
          <div
            className={tw`${featureExpand ? 'mt-3' : 'h-0'} overflow-hidden`}
          >
            <Link
              className='hover:text-f-primary block w-full whitespace-nowrap py-2 pl-3 text-xs font-medium hover:cursor-pointer sm:text-sm'
              href='/ai-music-generator'
              onClick={() => setMenuExpand(false)}
            >
              <Trans>AI Music Generator</Trans>
            </Link>
            <Link
              className='hover:text-f-primary block w-full whitespace-nowrap py-2 pl-3 text-xs font-medium hover:cursor-pointer sm:text-sm'
              href='/ai-rap-generator'
              onClick={() => setMenuExpand(false)}
            >
              <Trans>AI Rap Generator</Trans>
            </Link>
            <Link
              className='hover:text-f-primary block w-full whitespace-nowrap py-2 pl-3 text-xs font-medium hover:cursor-pointer sm:text-sm'
              href='/ai-cover-generator'
              onClick={() => setMenuExpand(false)}
            >
              <Trans>AI Cover Generator</Trans>
            </Link>
            <Link
              className='hover:text-f-primary block w-full whitespace-nowrap py-2 pl-3 text-xs font-medium hover:cursor-pointer sm:text-sm'
              href='/text-to-speech'
              onClick={() => setMenuExpand(false)}
            >
              <Trans>Text to Speech</Trans>
            </Link>
          </div>
        </div>
        <Link
          className='hover:text-f-primary w-full whitespace-nowrap border-b border-[#FFFFFF29] py-4 text-xs font-medium hover:cursor-pointer sm:text-sm'
          href='/pricing'
          onClick={() => setMenuExpand(false)}
        >
          <Trans>Pricing</Trans>
        </Link>
      </div>
    </div>
  )
}

export default function Header(props: DefaultHeaderProps) {
  const { style, className, transparentOnScrollTop0, locales } = props

  const scroll = useScroll()
  const { signUp, profileModal } = useAppUIModal()
  const signOut = useSignOut()

  const isScrollTop0 = useMemo(() => {
    if (transparentOnScrollTop0) {
      return scroll?.top === undefined ? true : scroll.top === 0
    }
    return false
  }, [transparentOnScrollTop0, scroll?.top])

  const handleProfile = () => {
    profileModal.show()
  }
  const handleSignIn = () => {
    signUp.show({ defaultShowSignUp: false })
  }
  const handleSignUp = () => {
    signUp.show({ defaultShowSignUp: true })
  }
  const handleSignOut = async () => {
    await signOut()
  }

  return (
    <header
      style={{
        ...style,
      }}
      className={tw`
        ${className}
        ${transparentOnScrollTop0 ? 'bg-transparent' : 'bg-f-other-1'}
        ${!isScrollTop0 ? 'bg-f-other-1' : ''} sticky left-0 top-0 z-50 w-full
      `}
    >
      {/* pc */}
      <Pc
        onProfile={handleProfile}
        onSignIn={handleSignIn}
        onSignOut={handleSignOut}
        onSignUp={handleSignUp}
        className='hidden sm:flex'
        locales={locales}
      />

      {/* mobile */}
      <Mobile
        onProfile={handleProfile}
        onSignIn={handleSignIn}
        onSignOut={handleSignOut}
        onSignUp={handleSignUp}
        className='flex sm:hidden'
      />
    </header>
  )
}
